/*********************************
	GENERAL UTILITY FUNCTIONS
*********************************/

import _ from 'lodash';

const regUdidNil = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const regUdid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const regGuid = /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/;

export function convertDateToNetIsoString(date: any): string {
	if (date instanceof Date) {
		const year = date.getUTCFullYear().toString().padStart(4, '0');
		const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
		const day = date.getUTCDate().toString().padStart(2, '0');
		const hours = date.getUTCHours().toString().padStart(2, '0');
		const minutes = date.getUTCMinutes().toString().padStart(2, '0');
		const seconds = date.getUTCSeconds().toString().padStart(2, '0');
		const milliseconds = date.getUTCMilliseconds().toString().padStart(3, '0');

		return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}0000`;
	}

	// Try to parse the input as a date string
	const parsedDate = Date.parse(date);

	if (!isNaN(parsedDate)) {
		return convertDateToNetIsoString(new Date(parsedDate));
	}

	// Unable to convert. Return the original input.
	return date;
}

/** Converts UTC string to locale date string, or returns empty string */
export function utcDateToLocaleDateString(utcDateString: any) {
	if (isDate(utcDateString) === false) return '';

	//EG: utcDateString  	= '2023-10-12T12:46:34.89' UTC+0000

	const parsedDate = new Date(utcDateString);
	//EG: parsedDate 		= Thu Oct 12 2023 14:46:34 GMT+0200 (Central European Summer Time)

	const newDate = new Date(parsedDate.getTime());
	//EG: newDate 			= Thu Oct 12 2023 14:46:34 GMT+0200 (Central European Summer Time)

	return newDate.toLocaleString();
	//EG: returns			= '10/12/2023, 2:46:34 PM'
}

export function isDefined<T>(value: T | undefined | null): value is T {
	return value !== undefined && value !== null;
}
export function isNotDefined<T>(value: T | undefined | null): value is T {
	return !isDefined(value);
}

export function isNothing<T>(obj: T | null | undefined): obj is null | undefined {
	return typeof obj === 'undefined' || obj === null;
}

export function isNotNothing<T>(obj: T | null | undefined): obj is null | undefined {
	return !isNothing<T>(obj);
}

export function isNilOrEmpty(value) {
	if (_.isNil(value)) return true;
	if (value === '') return true;
	return false;
}

export function isNotNilOrEmpty(value) {
	return !isNilOrEmpty(value);
}

export function isDate(value) {
	return !isNaN(new Date(value).getDate());
}

//Propercase. E.G: bla => Bla
export const pCase = (s: string | null | undefined): string | undefined => {
	if (!s) return undefined;
	if (s.length === 0) return s;

	let rest = s.length > 1 ? s.substring(1) : '';
	s = s[0].toUpperCase() + rest;

	return s;
};

//Checks if 2 objects are equal
export function isEqual(obj1, obj2) {
	//(c) 2021 Chris Ferdinandi, MIT License, https://gomakethings.com
	function getType(obj) {
		return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
	}

	function areArraysEqual() {
		// Check length
		if (obj1.length !== obj2.length) return false;

		// Check each item in the array
		for (let i = 0; i < obj1.length; i++) {
			if (!isEqual(obj1[i], obj2[i])) return false;
		}

		// If no errors, return true
		return true;
	}

	function areObjectsEqual() {
		if (Object.keys(obj1).length !== Object.keys(obj2).length) return false;

		// Check each item in the object
		for (let key in obj1) {
			if (Object.prototype.hasOwnProperty.call(obj1, key)) {
				if (!isEqual(obj1[key], obj2[key])) return false;
			}
		}

		// If no errors, return true
		return true;
	}

	function areFunctionsEqual() {
		return obj1.toString() === obj2.toString();
	}

	function arePrimativesEqual() {
		return obj1 === obj2;
	}

	// Get the object type
	let type = getType(obj1);

	// If the two items are not the same type, return false
	if (type !== getType(obj2)) return false;

	// Compare based on type
	if (type === 'array') return areArraysEqual();
	if (type === 'object') return areObjectsEqual();
	if (type === 'function') return areFunctionsEqual();
	return arePrimativesEqual();
}

export function isNotEqual(obj1, obj2) {
	return isEqual(obj1, obj2) === false;
}

//Converts strings that can be null to empty string
export function nullToEmpty(input: string | null): string {
	if (input === null) input = '';
	return input;
}

//Retrieves the properties in an object
export function getObjProps<TObj>(obj?: TObj) {
	return new Proxy(
		{},
		{
			get: (_, prop) => prop,
			set: () => {
				throw Error('Set not supported');
			},
		}
	) as {
		[P in keyof TObj]?: P;
	};
}

//Retrieves the properties in an object (hard)
export function getObjPropz<TObj>() {
	return new Proxy(
		{},
		{
			get: (_, prop) => prop,
			set: () => {
				throw Error('Set not supported');
			},
		}
	) as {
		[P in keyof TObj]: P;
	};
}

//Checks if string is GUID
export function isGuid(id: string | null | undefined) {
	if (isNothing(id)) return false;
	return regGuid.test(id);
}
export function isNoGuid(id: string | null | undefined) {
	return !isGuid(id);
}

//Checks if string is UDID
export function isUdid(id: string | null | undefined, checkNil: boolean = false) {
	if (isNothing(id)) {
		return false;
	}

	if (checkNil) {
		return regUdidNil.test(id);
	}

	return regUdid.test(id);
}

export function isNoUdid(id: string, checkNil: boolean = false) {
	return !isUdid(id, checkNil);
}

export function deepClone<T>(source: T): T {
	return Array.isArray(source)
		? source.map((item) => deepClone(item))
		: source instanceof Date
		? new Date(source.getTime())
		: source && typeof source === 'object'
		? Object.getOwnPropertyNames(source).reduce((o, prop) => {
				Object.defineProperty(o, prop, Object.getOwnPropertyDescriptor(source, prop)!);
				o[prop] = deepClone((source as { [key: string]: any })[prop]);
				return o;
		  }, Object.create(Object.getPrototypeOf(source)))
		: (source as T);
}

export function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
